import { useState } from "react"
import { Head, usePage } from "@inertiajs/react"
import PageTitle from "@/components/PageTitle"
import type { Market } from "@/lib/useMarket"
import Header from "./components/Header"
import MarketPicker from "./components/MarketPicker"
import PlaceholderPage from "./components/PlaceholderPage"
import Sidebar from "./components/Sidebar"
import { FALLBACK_MARKETS, GROUPS, type SidebarPage } from "./sidebarData"

type Props = { org_markets?: Market[]; user?: { name?: string; email: string } | null }

// Wireframe of the fixed sidebar: one list on every page, market picked in the
// header. The picked market stands in for the one stored in the session.
// Market pages ask "Which market?" until one is picked. Home and Revenue show
// the remembered market, or All markets when none is picked or when the
// viewer chooses All markets there.
export default function SidebarWireframe() {
  const { org_markets, user } = usePage<Props>().props
  const markets = org_markets?.length ? org_markets : FALLBACK_MARKETS

  const [page, setPage] = useState<SidebarPage>(GROUPS[0].pages[0])
  const [market, setMarket] = useState<Market | null>(null)
  const [allMarkets, setAllMarkets] = useState(false)

  const changeMarket = (m: Market | null) => {
    setAllMarkets(m === null)
    if (m) setMarket(m)
  }

  const needsPick = page.scope === "market" && !market
  const shownMarket = page.scope === "either" && allMarkets ? null : market

  return (
    <div className="flex h-screen flex-col">
      <Head title={page.label} />
      <div className="flex min-h-0 flex-1">
        <Sidebar active={page.key} onSelect={setPage} user={user ?? null} />
        <div className="flex min-w-0 flex-1 flex-col bg-muted/40">
          <Header page={page} markets={markets} market={shownMarket} onMarketChange={changeMarket} />
          <div className="flex flex-1 flex-col gap-4 overflow-auto p-6">
            <PageTitle title={page.label} subtitle={page.blurb} />
            {needsPick ? (
              <MarketPicker pageLabel={page.label} markets={markets} onPick={changeMarket} />
            ) : (
              <PlaceholderPage />
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-shrink-0 items-center gap-3 border-t border-border bg-background px-4 py-1.5 text-xs text-foreground">
        <span className="font-medium">Wireframe</span>
        <span>Remembered market: {market ? market.name : "none yet"}</span>
        {market && (
          <button
            type="button"
            onClick={() => { setMarket(null); setAllMarkets(false) }}
            className="text-link hover:underline"
          >
            Forget it (acts like a new session)
          </button>
        )}
      </div>
    </div>
  )
}
